import apiClient from './apiClient';
import type {
  ScheduledMessage,
  ScheduledMessageDetails,
  ScheduledMessagesSummary,
  PagedScheduledMessagesResponse,
  ScheduledOperationResponse
} from '../types';

/**
 * Service para interagir com a API de Mensagens Agendadas
 */
export const scheduledService = {
  /**
   * Lista mensagens agendadas
   */
  async listMessages(maxMessages: number = 100): Promise<ScheduledMessage[]> {
    const response = await apiClient.get<ScheduledMessage[]>('/scheduled', {
      params: { maxMessages }
    });
    return response.data;
  },

  /**
   * Lista mensagens agendadas com paginação
   */
  async listMessagesPaged(
    pageSize: number = 100,
    fromSequenceNumber?: number
  ): Promise<PagedScheduledMessagesResponse> {
    const response = await apiClient.get<PagedScheduledMessagesResponse>('/scheduled/paged', {
      params: {
        pageSize,
        fromSequenceNumber
      }
    });
    return response.data;
  },

  /**
   * Obtém resumo das mensagens agendadas
   */
  async getSummary(): Promise<ScheduledMessagesSummary> {
    const response = await apiClient.get<ScheduledMessagesSummary>('/scheduled/summary');
    return response.data;
  },

  /**
   * Obtém detalhes de uma mensagem agendada
   */
  async getMessageDetails(sequenceNumber: number): Promise<ScheduledMessageDetails> {
    const response = await apiClient.get<ScheduledMessageDetails>(`/scheduled/${sequenceNumber}`);
    return response.data;
  },

  /**
   * Cancela uma mensagem agendada
   */
  async cancelMessage(sequenceNumber: number): Promise<void> {
    await apiClient.delete(`/scheduled/${sequenceNumber}`);
  },

  /**
   * Cancela múltiplas mensagens agendadas
   */
  async cancelBatch(sequenceNumbers: number[]): Promise<{ cancelled: number; total: number }> {
    const response = await apiClient.post<{ cancelled: number; total: number }>('/scheduled/cancel-batch', {
      sequenceNumbers
    });
    return response.data;
  },

  /**
   * Cancela todas as mensagens agendadas (síncrono - pode dar timeout)
   */
  async cancelAll(): Promise<{ cancelled: number }> {
    const response = await apiClient.delete<{ cancelled: number }>('/scheduled/cancel-all');
    return response.data;
  },

  // ============================================================================
  // Operações Assíncronas (com Polling)
  // ============================================================================

  /**
   * [ASSÍNCRONO] Inicia operação de cancelar todas as mensagens agendadas
   * Retorna imediatamente com operationId para polling
   */
  async startCancelAllAsync(): Promise<ScheduledOperationResponse> {
    const response = await apiClient.post<ScheduledOperationResponse>('/scheduled/operations/cancel-all');
    return response.data;
  },

  /**
   * Obtém status de uma operação em andamento
   */
  async getOperationStatus(operationId: string): Promise<ScheduledOperationResponse> {
    const response = await apiClient.get<ScheduledOperationResponse>(`/scheduled/operations/${operationId}`);
    return response.data;
  },

  /**
   * Lista operações recentes
   */
  async getRecentOperations(limit: number = 20): Promise<ScheduledOperationResponse[]> {
    const response = await apiClient.get<ScheduledOperationResponse[]>('/scheduled/operations', {
      params: { limit }
    });
    return response.data;
  },

  /**
   * Cancela uma operação em andamento
   */
  async cancelOperation(operationId: string): Promise<void> {
    await apiClient.post(`/scheduled/operations/${operationId}/cancel`);
  },

  /**
   * Polling helper - aguarda até que a operação seja concluída
   * Chama callback com status atualizado a cada intervalo
   */
  async pollOperation(
    operationId: string,
    onUpdate: (operation: ScheduledOperationResponse) => void,
    intervalMs: number = 2000,
    maxAttempts: number = 300 // 10 minutos com 2s de intervalo
  ): Promise<ScheduledOperationResponse> {
    let attempts = 0;

    while (attempts < maxAttempts) {
      const operation = await this.getOperationStatus(operationId);
      onUpdate(operation);

      if (operation.status === 'Completed' || operation.status === 'Failed' || operation.status === 'Cancelled') {
        return operation;
      }

      await new Promise(resolve => setTimeout(resolve, intervalMs));
      attempts++;
    }

    throw new Error('Operation timeout - polling exceeded max attempts');
  }
};
